import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Settings, Palette, Moon, Sun, Monitor, Check } from 'lucide-react';

function Preferences() {
  const [theme, setTheme] = useState('auto');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const savedTheme = Cookies.get('theme') || 'auto';
    setTheme(savedTheme);
    applyTheme(savedTheme);
  }, []);

  const handleThemeChange = (selectedTheme) => {
    setTheme(selectedTheme);
    Cookies.set('theme', selectedTheme, { expires: 365 });
    applyTheme(selectedTheme);
    setSuccess(true);
    setTimeout(() => setSuccess(false), 3000);
  };

  const applyTheme = (theme) => {
    // Remove existing classes
    document.body.classList.remove('dark-mode', 'light-mode', 'bg-dark', 'text-white', 'bg-light', 'text-dark');
    document.documentElement.classList.remove('dark');
    
    if (theme === 'dark') {
      // Apply legacy CSS classes for compatibility and Tailwind's dark class
      document.body.classList.add('dark-mode');
      document.documentElement.classList.add('dark');
      // Let Tailwind handle the styling
      document.body.style.backgroundColor = '';
      document.body.style.color = '';
    } else if (theme === 'light') {
      document.body.classList.add('light-mode');
      // Let Tailwind handle the styling
      document.body.style.backgroundColor = '';
      document.body.style.color = '';
    }
  };

  const themeOptions = [
    {
      value: 'auto',
      label: 'Auto',
      description: 'Follow system preference',
      icon: Monitor
    },
    {
      value: 'light',
      label: 'Light',
      description: 'Always use light mode',
      icon: Sun
    },
    {
      value: 'dark',
      label: 'Dark',
      description: 'Always use dark mode',
      icon: Moon
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 flex items-center">
            <Settings className="w-8 h-8 mr-3" />
            Preferences
          </h1>
          <p className="text-gray-600 dark:text-gray-300">Customize your experience</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Theme Settings Card */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Palette className="w-5 h-5 mr-2" />
                Theme Settings
              </CardTitle>
              <CardDescription>
                Choose how the interface looks and feels
              </CardDescription>
            </CardHeader>
            <CardContent>
              {success && (
                <Alert className="mb-4">
                  <Check className="w-4 h-4" />
                  <AlertDescription>Theme preference saved successfully!</AlertDescription>
                </Alert>
              )}

              <div className="space-y-4">
                <Label className="text-base font-medium">Select Theme</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {themeOptions.map((option) => {
                    const IconComponent = option.icon;
                    const isSelected = theme === option.value;
                    
                    return (
                      <Card 
                        key={option.value}
                        className={`cursor-pointer transition-all ${
                          isSelected 
                            ? 'ring-2 ring-primary bg-primary/5' 
                            : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                        onClick={() => handleThemeChange(option.value)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center space-x-3">
                            <div className={`p-2 rounded-full ${
                              isSelected 
                                ? 'bg-primary text-primary-foreground' 
                                : 'bg-gray-100 dark:bg-gray-700'
                            }`}>
                              <IconComponent className="w-5 h-5" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <h3 className="font-medium">{option.label}</h3>
                                {isSelected && (
                                  <Check className="w-4 h-4 text-primary" />
                                )}
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {option.description}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Additional Settings Card */}
          <Card>
            <CardHeader>
              <CardTitle>Additional Settings</CardTitle>
              <CardDescription>
                More customization options coming soon
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-medium">Notifications</Label>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Email notifications for updates
                    </p>
                  </div>
                  <Button variant="outline" size="sm" disabled>
                    Coming Soon
                  </Button>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-medium">Auto-save</Label>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Automatically save changes
                    </p>
                  </div>
                  <Button variant="outline" size="sm" disabled>
                    Coming Soon
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Preview Card */}
          <Card>
            <CardHeader>
              <CardTitle>Preview</CardTitle>
              <CardDescription>
                See how your theme choice looks
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
                  <p className="text-sm font-medium">Sample Card</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    This is how content will appear with your current theme
                  </p>
                </div>
                <div className="flex space-x-2">
                  <Button size="sm" className="flex-1">Primary</Button>
                  <Button size="sm" variant="outline" className="flex-1">Secondary</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default Preferences;
